import type { OutcomeRowSummary } from '@/components/markets/market-row-card';
import { initialState, type PreviewSimState } from '@/lib/launchpad/preview-simulation';

export const PREVIEW_CONSENSUS_THRESHOLD_D9 = '660000000' as const;

/** Ticks at launchpad cadence before easing into a slower settle. */
export const SHOWCASE_CADENCE_FAST_SCALE = 1 as const;

/** Peak cadence multiplier for the last ticks of a burst (still calmer than the old flat 6×). */
export const SHOWCASE_CADENCE_SLOW_SCALE = 3 as const;

/** Completed ticks before the ramp from fast → slow begins. */
export const SHOWCASE_CADENCE_RAMP_START_TICK = 4 as const;

/** Per-tick cadence for the landing showcase: quick hook, then gradual settle. */
export function showcaseCadenceScaleForTick(completedTicks: number): number {
  if (completedTicks < SHOWCASE_CADENCE_RAMP_START_TICK) {
    return SHOWCASE_CADENCE_FAST_SCALE;
  }
  const rampSpan = Math.max(1, SHOWCASE_MAX_SIM_TICKS - SHOWCASE_CADENCE_RAMP_START_TICK);
  const progress = Math.min(
    1,
    (completedTicks - SHOWCASE_CADENCE_RAMP_START_TICK) / (rampSpan - 1),
  );
  return (
    SHOWCASE_CADENCE_FAST_SCALE +
    progress * (SHOWCASE_CADENCE_SLOW_SCALE - SHOWCASE_CADENCE_FAST_SCALE)
  );
}

/** Ticks per in-view session before the center card settles on its last state. */
export const SHOWCASE_MAX_SIM_TICKS = 10 as const;

function normalizeToUnitSum(shares: readonly number[]): number[] {
  const sum = shares.reduce((acc, share) => acc + share, 0);
  return shares.map((share) => share / sum);
}

export type ShowcaseMarketConfig = {
  pubkeySuffix: string;
  titleKey: 'centerMarketTitle' | 'leftMarketTitle' | 'rightMarketTitle';
  cover: string;
  location: string;
  category: string;
  outcomeLabels: readonly string[];
  imageUrls: readonly string[];
  displayShares: readonly number[];
  startingShares: number[];
  frozenState: PreviewSimState;
  animated?: boolean;
};

const REMOTE_DISPLAY_SHARES = [0.39, 0.31, 0.15, 0.14] as const;
const REMOTE_LABELS = [
  'Hybrid default',
  'Remote dominant',
  'Office returns',
  'Split by industry',
] as const;
const REMOTE_IMAGES = [
  '/images/support-mainnet/markets-showcase/outcome-hybrid.jpg',
  '/images/support-mainnet/markets-showcase/outcome-remote.jpg',
  '/images/support-mainnet/markets-showcase/outcome-office.jpg',
  '/images/support-mainnet/markets-showcase/outcome-split.jpg',
] as const;

const RAPPER_DISPLAY_SHARES = [0.44, 0.33, 0.23] as const;
const RAPPER_LABELS = ['Kendrick Lamar', 'Drake', 'Jay-Z'] as const;
const RAPPER_IMAGES = [
  '/images/support-mainnet/markets-showcase/rapper/outcome-kendrick.jpg',
  '/images/support-mainnet/markets-showcase/rapper/outcome-drake.jpg',
  '/images/support-mainnet/markets-showcase/rapper/outcome-jayz.jpg',
] as const;

const TRUMP_IRAN_DISPLAY_SHARES = [0.44, 0.33, 0.23] as const;
const TRUMP_IRAN_LABELS = ['To stop nukes', 'For oil', 'Israel made him'] as const;
const TRUMP_IRAN_IMAGES = [
  '/images/support-mainnet/markets-showcase/trump-iran/outcome-nukes.jpg',
  '/images/support-mainnet/markets-showcase/trump-iran/outcome-oil.jpg',
  '/images/support-mainnet/markets-showcase/trump-iran/outcome-israel.jpg',
] as const;

function buildMarketConfig(
  pubkeySuffix: string,
  titleKey: ShowcaseMarketConfig['titleKey'],
  cover: string,
  location: string,
  category: string,
  outcomeLabels: readonly string[],
  imageUrls: readonly string[],
  displayShares: readonly number[],
  animated?: boolean,
): ShowcaseMarketConfig {
  const startingShares = normalizeToUnitSum(displayShares);
  return {
    pubkeySuffix,
    titleKey,
    cover,
    location,
    category,
    outcomeLabels,
    imageUrls,
    displayShares,
    startingShares,
    frozenState: initialState(outcomeLabels.length, startingShares),
    ...(animated !== undefined ? { animated } : {}),
  };
}

export const CENTER_SHOWCASE_MARKET: ShowcaseMarketConfig = buildMarketConfig(
  'landing-showcase-center',
  'centerMarketTitle',
  '/images/support-mainnet/markets-showcase/trump-iran/cover.jpg',
  'US',
  'politics',
  TRUMP_IRAN_LABELS,
  TRUMP_IRAN_IMAGES,
  TRUMP_IRAN_DISPLAY_SHARES,
  true,
);

export const LEFT_SHOWCASE_MARKET: ShowcaseMarketConfig = buildMarketConfig(
  'landing-showcase-left',
  'leftMarketTitle',
  '/images/support-mainnet/markets-showcase/rapper/cover.jpg',
  'US',
  'culture',
  RAPPER_LABELS,
  RAPPER_IMAGES,
  RAPPER_DISPLAY_SHARES,
);

export const RIGHT_SHOWCASE_MARKET: ShowcaseMarketConfig = buildMarketConfig(
  'landing-showcase-right',
  'rightMarketTitle',
  '/images/support-mainnet/markets-showcase/cover.jpg',
  'Global',
  'culture',
  REMOTE_LABELS,
  REMOTE_IMAGES,
  REMOTE_DISPLAY_SHARES,
);

/** @deprecated Use CENTER_SHOWCASE_MARKET — kept for tests importing legacy names. */
export const LANDING_SHOWCASE_PUBKEY: string = CENTER_SHOWCASE_MARKET.pubkeySuffix;
/** @deprecated Use CENTER_SHOWCASE_MARKET.startingShares */
export const FIGMA_STARTING_SHARES: number[] = CENTER_SHOWCASE_MARKET.startingShares;
/** @deprecated Use CENTER_SHOWCASE_MARKET.frozenState */
export const FROZEN_SHOWCASE_STATE: PreviewSimState = CENTER_SHOWCASE_MARKET.frozenState;

export function buildShowcaseOutcomes(
  market: ShowcaseMarketConfig,
  sim: PreviewSimState,
): OutcomeRowSummary[] {
  return market.outcomeLabels.map((label, index) => {
    const simOutcome = sim.outcomes[index];
    return {
      label,
      share: simOutcome?.share ?? market.displayShares[index] ?? 0,
      originalIndex: index,
      effectiveMarketCapD6: simOutcome?.reservesD6 ?? '0',
      imageUrl: market.imageUrls[index] ?? null,
      imageNsfw: false,
    };
  });
}
